import api from "../utils/api";
import format from "date-fns/format";
import { API_BASE } from "../config";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  Collapse
} from "@mui/material";
import {
  Search as SearchIcon,
  Send as SendIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  Email as EmailIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
interface TicketData {
  _id: string;
  userName: string;
  userEmail: string;
  subject: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Message {
  _id: string;
  sender: string;
  message: string;
  timestamp: string;
  attachments?: Array<{
    _id: string;
    filename: string;
  }>;
}

const Ticket: React.FC = () => {
  const [tickets, setTickets] = useState<TicketData[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null);
  const [conversation, setConversation] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [imageURLs, setImageURLs] = useState<{ [key: string]: string }>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);



  const fetchTickets = useCallback(async (page: number = 1) => {
    try {
      setIsListLoading(true);
      const response = await api.get(`/api/adminTicket/tickets/all?page=${page}&limit=${pagination.limit}&search=${searchQuery}&status=${statusFilter}`);
      if (response.data.status === "success") {
        setTickets(response.data.tickets);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setIsListLoading(false);
    }
  }, [pagination.limit, searchQuery, statusFilter]);

  useEffect(() => {
    fetchTickets(pagination.page);
  }, [pagination.page, fetchTickets]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchQuery, statusFilter]);

  const fetchConversation = async (ticketId: string) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/adminTicket/tickets/${ticketId}/conversation`
      );
      setConversation(response.data.conversation.messages);
      await loadImages(response.data.conversation.messages);
    } catch (error) {
      console.error("Error fetching conversation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSelect = (ticket: TicketData) => {
    if (selectedTicket?._id === ticket._id) {
      setSelectedTicket(null);
    } else {
      setSelectedTicket(ticket);
      fetchConversation(ticket._id);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      await api.post(
        `/api/adminTicket/tickets/${selectedTicket._id}/reply`,
        { message: newMessage }
      );
      setNewMessage("");
      fetchConversation(selectedTicket._id);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    try {
      await api.post(`/api/adminTicket/tickets/${selectedTicket._id}/close`);
      await fetchTickets(pagination.page);
      setSelectedTicket(prev => prev ? { ...prev, status: "closed" } : null);
    } catch (error) {
      console.error("Error closing ticket:", error);
    }
  };

  const fetchImage = async (fileName: string): Promise<string | undefined> => {
    try {
      const response = await api.get(
        `/api/adminTicket/images/${fileName}`,
        {
          responseType: "blob",
        }
      );
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Error loading image:", error);
    }
  };

  const loadImages = async (messages: Message[]) => {
    const imagePromises = messages.flatMap(msg =>
      msg.attachments ? msg.attachments.map(async attachment => {
        const imageUrl = await fetchImage(attachment.filename);
        if (imageUrl) {
          setImageURLs(prev => ({ ...prev, [attachment.filename]: imageUrl }));
        }
      }) : []
    );
    await Promise.all(imagePromises);
  };


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "open": return "success";
      case "closed": return "error";
      default: return "warning";
    }
  };


  return (
    <Container maxWidth="lg" className="py-4">
      {/* Search and Filter Section */}
      <Paper elevation={3} className="mb-6 p-4 sticky top-0 z-10 bg-white">
        <Box className="flex gap-4 flex-wrap">
          <TextField
            className="flex-1"
            variant="outlined"
            size="small"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="text-gray-400" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" style={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Tickets</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" style={{ minWidth: 100 }}>
            <InputLabel>Limit</InputLabel>
            <Select
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
              label="Limit"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Tickets List */}
      <Box className="space-y-4">
        {isListLoading && tickets.length === 0 ? (
          <Box className="flex justify-center py-20">
            <CircularProgress sx={{ color: '#523680' }} />
          </Box>
        ) : tickets.length > 0 ? (
          tickets.map((ticket) => (
            <Paper
              key={ticket._id}
              elevation={2}
              className="overflow-hidden transition-all duration-200 hover:shadow-md"
            >
              {/* Ticket Header */}
              <Box
                className="p-3 md:p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleTicketSelect(ticket)}
              >
                <Box className="flex justify-between items-start gap-3">
                  <Box className="flex-1 min-w-0">
                    <Typography className="text-sm md:text-xl font-black text-gray-900 uppercase tracking-tight truncate mb-1">
                      {ticket.subject}
                    </Typography>
                    <Box className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2">
                      <Box className="flex items-center gap-1.5">
                        <div className="p-1 bg-blue-50 rounded-md">
                          <PersonIcon sx={{ fontSize: { xs: 12, md: 16 } }} className="text-blue-600" />
                        </div>
                        <Typography className="text-[10px] md:text-sm font-bold text-gray-600 truncate max-w-[120px] md:max-w-none uppercase">
                          {ticket.userName}
                        </Typography>
                      </Box>
                      <Box className="flex items-center gap-1.5">
                        <div className="p-1 bg-purple-50 rounded-md">
                          <EmailIcon sx={{ fontSize: { xs: 12, md: 16 } }} className="text-purple-600" />
                        </div>
                        <Typography className="text-[10px] md:text-sm font-bold text-gray-400 lowercase truncate max-w-[150px] md:max-w-none">
                          {ticket.userEmail}
                        </Typography>
                      </Box>
                      <Box className="flex items-center gap-1.5">
                        <div className="p-1 bg-gray-50 rounded-md">
                          <TimeIcon sx={{ fontSize: { xs: 12, md: 16 } }} className="text-gray-400" />
                        </div>
                        <Typography className="text-[10px] md:text-sm font-black text-gray-400 uppercase tracking-widest">
                          {format(new Date(ticket.createdAt), 'MMM dd, HH:mm')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box className="flex flex-col items-end gap-2 shrink-0">
                    <Chip
                      label={ticket.status}
                      color={getStatusColor(ticket.status)}
                      size="small"
                      className="min-w-[70px] md:min-w-[90px] font-black uppercase text-[9px] md:text-[11px] tracking-tighter"
                    />
                    {selectedTicket?._id === ticket._id ? (
                      <ExpandLessIcon className="text-gray-300" />
                    ) : (
                      <ExpandMoreIcon className="text-gray-300" />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Conversation Section */}
              <Collapse in={selectedTicket?._id === ticket._id}>
                <Divider />
                <Box className="bg-gray-50/50">
                  {/* Messages */}
                  <Box className="max-h-[350px] md:max-h-[500px] overflow-y-auto p-3 md:p-6 space-y-3">
                    {loading ? (
                      <Box className="flex justify-center items-center py-10 opacity-30">
                        <CircularProgress size={20} thickness={6} sx={{ color: '#523680' }} />
                      </Box>
                    ) : (
                      conversation.map((msg) => (
                        <Box
                          key={msg._id}
                          className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}
                        >
                          <Paper
                            elevation={0}
                            className={`max-w-[85%] md:max-w-[70%] p-2.5 md:p-4 border ${msg.sender === "admin"
                              ? "bg-[#523680] border-purple-900 rounded-2xl rounded-tr-none text-white shadow-lg shadow-purple-200/50"
                              : "bg-white border-gray-100 rounded-2xl rounded-tl-none text-gray-800 shadow-sm"
                              }`}
                          >
                            <div className="flex items-center justify-between gap-4 mb-1">
                              <Typography
                                className={`text-[9px] md:text-xs font-black uppercase tracking-widest ${msg.sender === "admin" ? "text-purple-200" : "text-blue-600"}`}
                              >
                                {msg.sender === "admin" ? "Official Reply" : msg.sender}
                              </Typography>
                              <Typography className={`text-[8px] md:text-[10px] font-bold uppercase tracking-tighter opacity-60 ${msg.sender === "admin" ? "text-white" : "text-gray-400"}`}>
                                {format(new Date(msg.timestamp), 'HH:mm')}
                              </Typography>
                            </div>

                            <Typography className="text-[11px] md:text-sm font-medium leading-relaxed">
                              {msg.message}
                            </Typography>

                            {msg.attachments && msg.attachments.length > 0 && (
                              <Box className="mt-3 flex flex-wrap gap-2">
                                {msg.attachments.map((attachment) => (
                                  <img
                                    key={attachment._id}
                                    src={imageURLs[attachment.filename] || ""}
                                    alt="attachment"
                                    className="w-16 h-16 md:w-32 md:h-32 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity border border-white/20"
                                    onClick={() => window.open(imageURLs[attachment.filename], "_blank")}
                                  />
                                ))}
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Message Input */}
                  {ticket.status !== "closed" && (
                    <Box className="p-3 md:p-6 bg-white border-t border-gray-100">
                      <Box className="flex gap-2 items-center">
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Type a quick reply..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          InputProps={{
                            sx: {
                              borderRadius: '12px',
                              fontSize: { xs: '12px', md: '14px' },
                              bgcolor: 'gray.50',
                              '& fieldset': { border: 'none' }
                            }
                          }}
                        />
                        <div className="flex gap-1">
                          <IconButton
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim()}
                            sx={{
                              bgcolor: '#523680',
                              color: 'white',
                              '&:hover': { bgcolor: '#3f2a60' },
                              '&.Mui-disabled': { bgcolor: 'gray.100', color: 'gray.400' },
                              borderRadius: '12px',
                              p: { xs: 1, md: 1.5 }
                            }}
                          >
                            <SendIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                          </IconButton>
                          <IconButton
                            onClick={handleCloseTicket}
                            title="Close Ticket"
                            sx={{
                              bgcolor: 'red.50',
                              color: 'red.400',
                              border: '1px solid',
                              borderColor: 'red.100',
                              '&:hover': { bgcolor: 'red.100' },
                              borderRadius: '12px',
                              p: { xs: 1, md: 1.5 }
                            }}
                          >
                            <CloseIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                          </IconButton>
                        </div>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))
        ) : (
          <Paper elevation={0} className="p-10 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
            <Typography className="text-gray-400 font-bold uppercase tracking-widest">No tickets found</Typography>
          </Paper>
        )}

        {/* Pagination Controls */}
        {!isListLoading && tickets.length > 0 && (
          <Box className="flex flex-col md:flex-row justify-center items-center gap-4 mt-8 mb-10">
            <Box className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700 text-sm"
              >
                Previous
              </button>

              <Box className="flex items-center space-x-1">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let start = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
                  let end = Math.min(pagination.totalPages, start + maxVisible - 1);

                  if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                  }

                  if (start > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                        className="w-8 h-8 md:w-10 md:h-10 rounded-lg border hover:bg-gray-50 transition-colors text-gray-600 text-sm"
                      >
                        1
                      </button>
                    );
                    if (start > 2) pages.push(<span key="dots-start" className="px-1 text-gray-400">...</span>);
                  }

                  for (let i = start; i <= end; i++) {
                    if (i <= 0) continue;
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setPagination(prev => ({ ...prev, page: i }))}
                        className={`w-8 h-8 md:w-10 md:h-10 rounded-lg border transition-all text-sm ${pagination.page === i
                          ? "bg-[#523680] text-white border-[#523680] shadow-md transform scale-105"
                          : "hover:bg-gray-50 text-gray-700"
                          }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (end < pagination.totalPages) {
                    if (end < pagination.totalPages - 1) pages.push(<span key="dots-end" className="px-1 text-gray-400">...</span>);
                    pages.push(
                      <button
                        key={pagination.totalPages}
                        onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                        className="w-8 h-8 md:w-10 md:h-10 rounded-lg border hover:bg-gray-50 transition-colors text-gray-600 text-sm"
                      >
                        {pagination.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </Box>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700 text-sm"
              >
                Next
              </button>
            </Box>

            <Box className="flex items-center space-x-2 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border shadow-sm">
              <span>Go to:</span>
              <input
                type="number"
                min="1"
                max={pagination.totalPages}
                className="w-12 p-1 border rounded text-center focus:ring focus:ring-blue-100 outline-none"
                placeholder="#"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const p = parseInt((e.target as HTMLInputElement).value);
                    if (!isNaN(p) && p >= 1 && p <= pagination.totalPages) {
                      setPagination(prev => ({ ...prev, page: p }));
                    }
                  }
                }}
              />
              <span className="whitespace-nowrap">of {pagination.totalPages} ({pagination.total} total)</span>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Ticket;